import { createSlice } from '@reduxjs/toolkit';

export interface PromoNotification {
  id: number;
  playerGUID?: string;
  type?: string;
  heading?: string;
  message: string;
  isClaimed: number;
  isExpired: number;
  isRead: number;
  timestamp: number;
}

export interface RecentTable {
  gameType: string;
  gameID: string;
  time: number;
}

const initialState = {
  playerId: 0,
  totalWin: 0,
  points: 0,
  playerName: '',
  displayName: '',
  playerRank: '',
  playerBank: '',
  playerBankAmount: 0,
  playerChargeBalance: 0,
  playerAvatar: '',
  playerEmail: '',
  playerLevel: 0,
  playerLevelStr: '',
  playerLevelPrg: 0,
  playerLevelPoints: 0,
  playerGold: 0,
  // Lifetime hands played — arrives on the got_playerMoney payload
  // (engine: stats.handsplayed). Drives the cashier tier display.
  handsPlayed: 0,
  playerDealerId: 0,
  playerFbId: '',
  playerGUID: '',
  loadVocab: '',
  dailyBonus: 0,
  token: '',
  ip: '',
  interface: {},
  playerCountry: '',
  playerLanguage: '',
  guest: true,
  playerCharged: false,
  playerChipsAdded: false,
  promoNotifications: [] as PromoNotification[],
  recentTables: [] as RecentTable[],
  handRank: {
    name: 'High Card',
    value: 0
  },
  isRestrictedFlag: 1,
  errorMsg: {
    msg: '',
    // Modal heading. Callers set an informational title (e.g. 'Buy-in' for a
    // seat-taken join) so the modal isn't always framed as an "error"; empty
    // falls back to the generic heading in ErrorMsgModal.
    title: '',
    addFunds: false,
    // Drives the global ErrorMsgModal's visibility from redux so it works in
    // every view — including each multi-table grid cell, which mounts its own
    // GlobalProvider and so couldn't see the old context-scoped open flag.
    open: false
  },
  // Extended profile (hydrated from /api/searchPlayer)
  profileLoaded: false,
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  countryCode: 'US',
  walletAddress: '',
  withdrawalWalletAddress: '',
  idVerified: 'PENDING',
  verifiedEmail: false,
  verifiedPhone: false,
  // null = not yet loaded or new user (attribute never set); true/false = explicit value
  enableSecureLogin: null as boolean | null
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    updateBankAmount: (state, action) => {
      // Ensure playerBankAmount is stored as a number, removing any formatting
      const rawAmount = action.payload?.response?.playWinpot;
      if (rawAmount !== undefined && rawAmount !== null) {
        if (typeof rawAmount === 'string') {
          state.playerBankAmount = parseFloat(rawAmount.replace(/,/g, '')) || 0;
        } else {
          state.playerBankAmount = parseFloat(rawAmount) || 0;
        }
      }

      // Handle charge balance similarly
      const rawCharge = action.payload?.response?.playChargeBalance;
      if (rawCharge !== undefined && rawCharge !== null) {
        if (typeof rawCharge === 'string') {
          state.playerChargeBalance = parseFloat(rawCharge.replace(/,/g, '')) || 0;
        } else {
          state.playerChargeBalance = parseFloat(rawCharge) || 0;
        }
      }

      // Lifetime hands played (engine sends stats.handsplayed on got_playerMoney).
      // The cashier tier display reads this; without capturing it here it stayed
      // at 0 and the tier always rendered as Tier 1.
      const rawHands = action.payload?.response?.handsplayed;
      if (rawHands !== undefined && rawHands !== null) {
        const parsed =
          typeof rawHands === 'string'
            ? parseInt(rawHands.replace(/,/g, ''), 10)
            : Number(rawHands);
        if (Number.isFinite(parsed)) state.handsPlayed = parsed;
      }

      if (action.payload?.promoNotifications !== undefined) {
        state.promoNotifications = action.payload?.promoNotifications;
      }
      // console.log(action.payload?.promoNotifications);

      // got_playerMoney also carries the player's recent tables list — most-recent first.
      // Schema: [{ gameType: 'texas' | 'omaha', gameID: '171', time: <unix-secs> }]
      const recent = action.payload?.response?.recentTables;
      if (Array.isArray(recent)) {
        state.recentTables = recent
          .filter((r: any) => r && r.gameID !== null && r.gameID !== undefined && r.gameType)
          .map((r: any) => ({
            gameType: String(r.gameType),
            gameID: String(r.gameID),
            time: Number(r.time) || 0
          }));
      }
    },
    updateAvatar: (state, action) => {
      state.playerAvatar = action.payload?.playerAvatar;
    },
    updateDisplayName: (state, action) => {
      state.displayName = action.payload || '';
    },
    updateCharged: (state, action) => {
      state.playerCharged = action.payload?.playerGUID === state.playerGUID;
    },
    updateUser: (state, action) => {
      // Reset profile PII before applying the login payload so a previous
      // player's data (firstName / address / idVerified / wallet etc.) does
      // not leak across an in-tab account switch. `usePlayerProfile.fetchProfile`
      // repopulates these for the new player on cashier mount.
      return {
        ...state,
        profileLoaded: false,
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        countryCode: 'US',
        walletAddress: '',
        withdrawalWalletAddress: '',
        idVerified: 'PENDING',
        verifiedEmail: false,
        verifiedPhone: false,
        enableSecureLogin: null,
        ...(action.payload?.response ?? {}),
        displayName: action.payload?.response?.displayName
          ? decodeURIComponent(action.payload.response.displayName)
          : '',
        isRestrictedFlag: action.payload?.response.isRestrictedFlag
      };
    },
    updateHandRank: (state, action) => {
      state.handRank = action.payload;
    },
    updateErrorMsg: (state, action) => {
      // Merge so partial updates work: the join-error handler sets
      // { msg, open: true }; the modal's OK clears with { open: false }
      // without wiping the message text.
      state.errorMsg = { ...state.errorMsg, ...action.payload };
    },
    setPlayerProfile: (state, action) => {
      // Soft-merge: only overwrite fields the caller explicitly provided.
      // Prevents an incomplete fetch (e.g. Descope-only response) from
      // clobbering values that were already populated at login or by an
      // earlier fetch (e.g. withdrawalWalletAddress from the DB path).
      const p = action.payload || {};
      state.profileLoaded = true;
      Object.keys(p).forEach((key) => {
        const value = (p as any)[key];
        if (value !== undefined) {
          (state as any)[key] = value;
        }
      });
    },
    patchPlayerProfile: (state, action) => {
      Object.assign(state, action.payload || {});
    },
    markPromoNotificationRead: (state, action) => {
      const notification = state.promoNotifications.find((item) => item.id === action.payload);
      if (notification) {
        notification.isRead = 1;
      }
    },
    setPromoNotifications: (state, action) => {
      state.promoNotifications = action.payload || [];
    }
  }
});

export const {
  updateBankAmount,
  updateAvatar,
  updateDisplayName,
  updateCharged,
  updateUser,
  updateHandRank,
  updateErrorMsg,
  setPlayerProfile,
  patchPlayerProfile,
  markPromoNotificationRead,
  setPromoNotifications
} = userSlice.actions;

export default userSlice.reducer;
